import {noop} from '@benchling/js-utils';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import ContainerSchemaManager from 'models/managers/ContainerSchemaManager';
import {
  forwardSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import * as NoteEditorTableUtil from 'notebook/notes/plugins/plain-table/NoteEditorTableUtil';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import toInsertableColumnInfos from 'notebook/structured-tables/columns/toInsertableColumnInfos';
import RefreshStructuredTableButton from 'notebook/structured-tables/components/RefreshStructuredTableButton';
import {
  SchemaFieldColumnUpdate,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {TableHeaderControlsArgs} from 'notebook/tables/constants/TableConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {
  handleRefreshTableColumns,
  mergeColumnUpdatesIntoColumnConstraints,
} from 'notebook/tables/refresh-table-data/LegacyRefreshTableData';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

export interface TransferTableConfigProps extends MediocreSearchResultProps {
  fileId: string;
  icon: BenchlingIconName;
  isMaximized?: boolean;
  tableTypeLabel: string;
  tableName: string;
  containerSchema: ContainerSchema;
  parentStorableIsGrid: boolean;
  structuredTableColumns: Array<StructuredTableColumn>;
  provenanceUuid: string;
  // From NoteEditor
  cid: string;
  indentation: number;
  index: number;
  mouseDownHandler: (index: number, e: BasicEvent) => void;
  mouseOverHandler: (index: number, e: BasicEvent) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateHandler: (index: number, content: any) => void;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  computedTable: Array<Array<EvaluatedCell>>;
  cellColors: object;
  multiEditing: boolean;
  tableNamespace: string;
  showExcessLabels: boolean;
  metadataForRestore?: MetadataForRestore<TableIndex> | null;
}

interface ConnectedProps {
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}

class TransferTableConfigConnector extends DataConnector<TransferTableConfigProps, ConnectedProps> {
  override stores = [SchemaFieldSelectorStore];

  enhanceProps(): ConnectedProps {
    return {
      selectorsById: this.getOrFetchMultipleRequired(
        ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
        getSelectorIdsFromFields(this.props.containerSchema.fields)
      ),
    };
  }
}

/**
 * This is a legacy transfer table config, not a StructuredTable.
 *
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export class TransferTableConfig extends React.PureComponent<TransferTableConfigProps & ConnectedProps> {
  static selectorsLink = {
    // selectors are not enabled, but TableEditable still requires this
    value: [],
    selectorUsageCounts: {},
  };

  _getDefaultStructuredTableColumns(containerSchema: ContainerSchema): Array<StructuredTableColumn> {
    return TransferTableUtil.getDefaultStructuredTableColumns({
      containerSchema,
      parentStorableIsGrid: this.props.parentStorableIsGrid,
      selectorsById: this.props.selectorsById,
      fileId: this.props.fileId,
    });
  }

  _structuredTableColumnToColumnDefinition = (
    structuredTableColumn: StructuredTableColumn,
    containerSchema: ContainerSchema
  ): ColumnDefinition =>
    TransferTableUtil.structuredTableColumnToColumnDefinition(
      structuredTableColumn,
      containerSchema,
      this.props.parentStorableIsGrid,
      [],
      true
    );

  _renderHeaderControls({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null {
    if (isCollapsed) {
      return null;
    }
    const doRefresh = async (): Promise<void> => {
      await handleRefreshTableColumns({
        entryOrTemplateId: this.props.fileId,
        provenanceUuid: this.props.provenanceUuid,
        onUpdateTable: ({
          columnUpdates,
        }: {
          columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
        }) => {
          const table = TransferTableUtil.createTableForConfig(
            this.props.tableName,
            this.props.structuredTableColumns,
            this.props.containerSchema,
            this.props.parentStorableIsGrid
          );
          const columnConstraints = mergeColumnUpdatesIntoColumnConstraints(
            table,
            columnUpdates,
            this.props.editorProps.getIdFromCid
          );
          const newTable = {...table, columnConstraints};
          this.props.updateHandler(this.props.index, {table: newTable});
        },
      });
    };

    return (
      <span className="u-vertical-center-items">
        <RefreshStructuredTableButton
          entryOrTemplateId={this.props.editorProps.fileId}
          doRefresh={doRefresh}
          structuredTableKind="OLD Transfer table config"
          disabled={this.props.editorProps.locked}
        />
      </span>
    );
  }

  override render(): JSX.Element {
    const allEditorProps = NoteEditorTableUtil.tableEditablePropsFromEditor(
      this.props.editor,
      this.props.editorProps
    );

    return (
      <TableEditable
        {...allEditorProps}
        canAddRemoveColumns={true}
        cid={this.props.cid}
        computedTable={this.props.computedTable}
        disableFormulas={false}
        disableLegacyHeader={true}
        headerControls={(args) => this._renderHeaderControls(args)}
        icon={this.props.icon}
        indentation={this.props.indentation}
        index={this.props.index}
        insertableColumnInfos={(() => {
          const defaultColumns = this._getDefaultStructuredTableColumns(this.props.containerSchema).map(
            (structuredTableColumn) => ({
              structuredTableColumn,
              columnDefinition: this._structuredTableColumnToColumnDefinition(
                structuredTableColumn,
                this.props.containerSchema
              ),
            })
          );
          return toInsertableColumnInfos(defaultColumns, this.props.structuredTableColumns);
        })()}
        isLeftAlignColumnHeader={true}
        isMaximized={this.props.isMaximized}
        disableMergingCells={true}
        mouseDownHandler={this.props.mouseDownHandler}
        mouseOverHandler={this.props.mouseOverHandler}
        multiEditing={this.props.multiEditing}
        metadataForRestore={this.props.metadataForRestore}
        openManageSelectorsModal={noop}
        selectorsEnabled={false}
        selectorsLink={TransferTableConfig.selectorsLink}
        showExcessLabels={this.props.showExcessLabels}
        showFormulaBox={true}
        table={TransferTableUtil.createTableForConfig(
          this.props.tableName,
          this.props.structuredTableColumns,
          this.props.containerSchema,
          this.props.parentStorableIsGrid
        )}
        tableLabel={this.props.tableTypeLabel}
        tableNamespace={this.props.tableNamespace}
        updateHandler={this.props.updateHandler}
        {...forwardSearchResultProps(this.props)}
      />
    );
  }
}

/**
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export default TransferTableConfigConnector.connect(TransferTableConfig);
